/**
 * Story Presentation Mode Component
 *
 * Full-screen presentation view for demo/pitch scenarios.
 * Features:
 * - Large, readable text display
 * - Speaker/character display with portrait
 * - Choice buttons for player interaction
 * - Navigation controls (next, previous, quit)
 * - Professional styling for audience viewing
 *
 * This is the public-facing interface for playing through a story.
 * Designed for clarity and focus, with minimal UI chrome.
 *
 * @module playMode/PresentationMode
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { TraversalEngine } from './TraversalEngine';
import type { TraversalResult, StoryGraph, StoryNode } from './TraversalEngine';
import { catalogStore, useCatalog } from '../../tilemap/store/catalogStore';

/**
 * Presentation Mode Props
 */
interface PresentationModeProps {
  /** The story graph to present */
  graph: StoryGraph;

  /** Optional background image URL */
  backgroundImage?: string;

  /**
   * The project this story belongs to. Only needed to resolve a `tilemap_ref`
   * node's scene slate (its map's thumbnail, via the tilemap catalog) —
   * dialogue-only presentations can omit it.
   */
  projectUuid?: string;

  /** Callback when presentation ends/exits */
  onClose?: () => void;
}

/**
 * Presentation Mode Component
 *
 * Full-screen story presentation interface.
 * Optimized for:
 * - Readable text at a distance
 * - Clear choice presentation
 * - Professional appearance
 * - Touch/keyboard navigation
 *
 * **Layout:**
 * - Top: Exit button
 * - Center: Character portrait (if available) and dialogue text
 * - Bottom: Choice buttons or next/end prompt
 *
 * **Keyboard shortcuts:**
 * - ESC or Q: Quit
 * - Arrow keys: Navigate choices
 * - Enter: Select choice or continue
 * - Space: Auto-advance when available
 */
export const PresentationMode: React.FC<PresentationModeProps> = ({
  graph,
  backgroundImage,
  projectUuid,
  onClose,
}) => {
  const [engine, setEngine] = useState<TraversalEngine | null>(null);
  const [currentResult, setCurrentResult] = useState<TraversalResult | null>(null);
  const [selectedChoiceIndex, setSelectedChoiceIndex] = useState(0);
  const [showExitDialog, setShowExitDialog] = useState(false);

  /**
   * Get the current node data for display
   */
  const currentNode = useMemo(() => {
    if (!currentResult) return null;
    return graph.nodes.get(currentResult.currentNodeId);
  }, [currentResult, graph]);

  /**
   * Scene slate: when the traversal is paused on a `tilemap_ref` node
   * (_docs/tilemap/04 §4's "pass-through" node — TraversalEngine.ts routes it
   * through the same auto-advance/choice logic as a Fragment), resolve its
   * referenced map's thumbnail from the tilemap catalog and show it full-bleed
   * in place of dialogue text. A ref with no assigned map, or no projectUuid
   * to resolve against, simply renders nothing here — never an error.
   */
  const isTilemapSlate = currentNode?.type === 'tilemap_ref';
  const tilemapUuid = isTilemapSlate ? (currentNode?.data.tilemap_uuid as string | undefined) : undefined;

  useEffect(() => {
    if (projectUuid) catalogStore.getState().ensure(projectUuid);
  }, [projectUuid]);

  const sceneMap = useCatalog((s) =>
    projectUuid && tilemapUuid ? s.byProject[projectUuid]?.find((m) => m.uuid === tilemapUuid) : undefined,
  );

  /**
   * Initialize presentation on mount
   */
  React.useEffect(() => {
    const entryNodeId = graph.entryNodeId || Array.from(graph.nodes.values())[0]?.id;
    if (!entryNodeId) {
      console.error('No entry node found');
      return;
    }

    const newEngine = new TraversalEngine(graph, {
      trackHistory: true,
      maxSteps: 10000,
    });

    const result = newEngine.startPlaythrough(entryNodeId);
    setEngine(newEngine);
    setCurrentResult(result);
    setSelectedChoiceIndex(0);
  }, [graph]);

  /**
   * Make a choice and advance
   */
  const handleMakeChoice = useCallback(
    (targetNodeId: string) => {
      if (!engine) return;
      const result = engine.makeChoice(targetNodeId);
      setCurrentResult(result);
      setSelectedChoiceIndex(0);
    },
    [engine]
  );

  /**
   * Handle keyboard input
   *
   * Supports arrow keys for navigation and enter to select.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'q' || e.key === 'Q') {
        setShowExitDialog(true);
      } else if (e.key === ' ' && currentResult && currentResult.choices?.length === 1) {
        e.preventDefault();
        handleMakeChoice(currentResult.choices[0].targetId);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentResult?.choices) {
          setSelectedChoiceIndex((i) => (i + 1) % currentResult.choices!.length);
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentResult?.choices) {
          setSelectedChoiceIndex((i) =>
            i === 0 ? currentResult.choices!.length - 1 : i - 1
          );
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (currentResult?.choices?.[selectedChoiceIndex]) {
          handleMakeChoice(currentResult.choices[selectedChoiceIndex].targetId);
        }
      }
    },
    [currentResult, selectedChoiceIndex, handleMakeChoice]
  );

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown as any);
    return () => window.removeEventListener('keydown', handleKeyDown as any);
  }, [handleKeyDown]);

  const handleExit = useCallback(() => {
    setShowExitDialog(false);
    onClose?.();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-gradient-to-b from-slate-900 to-slate-800 text-white flex flex-col overflow-hidden"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Background image (optional) */}
      {backgroundImage && (
        <BackgroundImage
          src={backgroundImage}
          className="absolute inset-0 opacity-20"
        />
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-4 flex justify-between items-center border-b border-slate-700">
          <h1 className="text-xl font-semibold">
            {isTilemapSlate ? (sceneMap?.name || (currentNode?.data.caption as string) || 'Story') : (currentNode?.data.name || 'Story')}
          </h1>
          <button
            onClick={() => setShowExitDialog(true)}
            title="Exit (ESC)"
            className="text-white text-2xl hover:opacity-75"
          >
            ✕
          </button>
        </div>

        {/* Main content area */}
        {isTilemapSlate ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 overflow-auto w-full">
            {sceneMap?.thumbnail_uuid ? (
              <img
                src={`/api/assets/${sceneMap.thumbnail_uuid}`}
                alt={sceneMap.name}
                className="max-w-full max-h-full rounded shadow-lg"
                style={{ imageRendering: 'pixelated' }}
              />
            ) : (
              <div className="text-center text-gray-400">
                <div className="text-5xl mb-3">🗺️</div>
                <p>{sceneMap?.name ?? 'Tile map'}</p>
              </div>
            )}
            {sceneMap?.name && <h2 className="text-lg font-medium text-blue-300 mt-4">{sceneMap.name}</h2>}
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-auto max-w-2xl mx-auto w-full">
            {/* Character portrait (if available) */}
            {currentNode?.data.speakerPortrait && (
              <div className="w-32 h-32 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden mb-6">
                <img
                  src={currentNode.data.speakerPortrait}
                  alt={currentNode.data.speakerName || 'Speaker'}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Speaker name */}
            {currentNode?.data.speakerName && (
              <h2 className="text-2xl font-bold text-blue-300 mb-4">
                {currentNode.data.speakerName}
              </h2>
            )}

            {/* Main dialogue text */}
            <p
              className="text-center leading-relaxed text-xl mb-4"
            >
              {currentNode?.data.text || 'Continue...'}
            </p>

            {/* Stage directions (if available) */}
            {currentNode?.data.stageDirections && (
              <p
                className="text-center italic text-gray-400 max-w-2xl text-sm"
              >
                *{currentNode.data.stageDirections}*
              </p>
            )}
          </div>
        )}

        {/* Choices or continue prompt */}
        <div className="px-6 py-8 border-t border-slate-700 bg-slate-900/50 backdrop-blur max-w-2xl w-full mx-auto">
          {currentResult?.choices && currentResult.choices.length > 0 ? (
            <div className="space-y-4">
              {/* Instruction text */}
              <p className="text-sm text-gray-400 text-center">
                {currentResult.choices.length === 1
                  ? 'Press SPACE or click to continue'
                  : 'Use ARROW KEYS to select, ENTER to choose'}
              </p>

              {/* Choice buttons */}
              <div className="space-y-2">
                {currentResult.choices.map((choice, idx) => (
                  <button
                    key={choice.targetId}
                    onClick={() => handleMakeChoice(choice.targetId)}
                    disabled={!choice.conditionMet}
                    className={`w-full text-lg py-3 px-4 rounded transition-all ${
                      idx === selectedChoiceIndex
                        ? 'bg-blue-600 ring-2 ring-blue-400 text-white'
                        : choice.conditionMet
                        ? 'bg-slate-700 hover:bg-slate-600 text-white'
                        : 'bg-slate-700 opacity-50 cursor-not-allowed text-gray-400'
                    }`}
                  >
                    {choice.label}
                    {!choice.conditionMet && (
                      <span className="text-xs text-gray-400 ml-2">
                        (unavailable)
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-center">
              <h2 className="text-2xl font-bold">End of Story</h2>
              <button
                onClick={() => setShowExitDialog(true)}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded text-lg font-medium"
              >
                🏠 Return to Editor
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Exit confirmation dialog */}
      {showExitDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-slate-800 rounded-lg p-6 max-w-sm mx-4">
            <h2 className="text-xl font-bold mb-4">Exit Presentation?</h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to exit? Your progress will not be saved.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowExitDialog(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded"
              >
                Continue Reading
              </button>
              <button
                onClick={handleExit}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PresentationMode.displayName = 'PresentationMode';
